import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getPublicAssetPath } from '../config';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const { darkMode } = useTheme();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="fa-solid fa-spinner fa-spin text-violet-600 text-3xl mb-4"></i>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden transition-colors duration-300 ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-violet-50 via-white to-violet-50/50'
    }`}>
      {/* Beautiful Background Pattern */}
      <div className="absolute inset-0 z-0">
        {/* Gradient Mesh Background */}
        <div className={`absolute inset-0 ${
          darkMode 
            ? 'bg-gradient-to-br from-violet-900/20 via-transparent to-violet-900/30' 
            : 'bg-gradient-to-br from-violet-100/20 via-transparent to-violet-100/30'
        }`}></div>
        
        {/* Subtle geometric pattern */}
        <div className={`absolute inset-0 ${darkMode ? 'opacity-[0.05]' : 'opacity-[0.03]'}`}>
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke={darkMode ? '#8b5cf6' : '#8b5cf6'} strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Decorative circles/blobs */}
        <div className={`absolute top-0 right-0 w-96 h-96 ${darkMode ? 'bg-violet-900/20' : 'bg-violet-200/10'} rounded-full blur-3xl`}></div>
        <div className={`absolute bottom-0 left-0 w-96 h-96 ${darkMode ? 'bg-violet-800/20' : 'bg-violet-300/10'} rounded-full blur-3xl`}></div>
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] ${darkMode ? 'bg-violet-900/10' : 'bg-violet-100/5'} rounded-full blur-3xl`}></div>
        
        {/* Subtle wave pattern at bottom */}
        <div className={`absolute bottom-0 left-0 right-0 h-64 ${darkMode ? 'opacity-10' : 'opacity-5'}`}>
          <svg viewBox="0 0 1440 320" className="w-full h-full" preserveAspectRatio="none">
            <path fill="#8b5cf6" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 z-10">
        <div className="w-full max-w-md">
          {/* Logo dan Brand */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center mb-6">
              <img 
                src={getPublicAssetPath('/le_long.png')} 
                alt="Legitimu Logo" 
                className={`h-14 transition-all ${darkMode ? 'brightness-0 invert filter' : ''}`}
                style={darkMode ? { filter: 'brightness(0) invert(1)' } : {}}
              />
            </div>
            <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Welcome Back</h1>
            <p className={`text-base mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Sign in to continue to your workspace
            </p>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>or</span>
            </div>
            <Link
              to="/"
              className={`inline-flex items-center justify-center text-sm font-medium transition-colors ${
                darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'
              }`}
            >
              Back to Landing Page
            </Link>
          </div>

          {/* Login Card */}
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-2xl shadow-xl border p-8 sm:p-10 transition-colors duration-300`}>
            {error && (
              <div className={`mb-6 p-4 ${darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-500'} border-l-4 rounded-lg`}>
                <p className={`text-sm flex items-center ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                  <i className="fa-solid fa-exclamation-circle mr-2"></i>
                  {error}
                </p>
              </div>
            )}
            
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email Field */}
              <div>
                <label htmlFor="email" className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} fa-solid fa-envelope text-sm`}></i>
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`block w-full pl-11 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm transition-all focus:bg-opacity-100 ${
                      darkMode 
                        ? 'border-gray-700 bg-gray-900 text-white placeholder-gray-500 focus:bg-gray-900' 
                        : 'border-gray-200 bg-gray-50 focus:bg-white'
                    }`}
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} fa-solid fa-lock text-sm`}></i>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`block w-full pl-11 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm transition-all focus:bg-opacity-100 ${
                      darkMode 
                        ? 'border-gray-700 bg-gray-900 text-white placeholder-gray-500 focus:bg-gray-900' 
                        : 'border-gray-200 bg-gray-50 focus:bg-white'
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute inset-y-0 right-0 pr-4 flex items-center transition-colors ${
                      darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {isLoading ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-arrow-right-to-bracket mr-2"></i>
                      Sign In
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Footer Text */}
          <div className="mt-8 text-center">
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Need assistance?{' '}
              <Link 
                to="/support" 
                className={`font-medium transition-colors ${
                  darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'
                }`}
              >
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

